var __rest = this && this.__rest || function (s, e) {
  var t = {};
  for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0) t[p] = s[p];
  if (s != null && typeof Object.getOwnPropertySymbols === "function") for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
    if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i])) t[p[i]] = s[p[i]];
  }
  return t;
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Callout, Collapsible, getTestId, Input, isValidObject, StateConfigContext } from '@airtel/air-design-system';
import { debounce, merge } from 'lodash';
import { useCallback, useContext, useEffect, useMemo, useReducer, useState } from 'react';
import { Keyboard, View } from 'react-native';
import { ButtonWidget } from '../../../button-widget';
import { LinkListItemWidget } from '../../../link-list-item-widget';
import { getSkycFormWidgetStyles } from '../../SkycFormWidgetStyles';
import { currentAddressDetailsConfig, localReferenceDetailsConfig, successAvatarConfig } from './AddressWidgetConfig';
import { AADHAAR_DETAILS_ACTIONS } from './AddressWidgetConstants';
import { getAddressStyles } from './AddressWidgetStyles';
import { getIsDisabledState, getSubHeading, getValuesFromAddressDetails, validateInput } from './AddressWidgetUtils';
export var AddressWidget = props => {
  var _a;
  var _useContext = useContext(StateConfigContext),
    theme = _useContext.theme;
  var _useMemo = useMemo(() => (props === null || props === void 0 ? void 0 : props.variant) === 'LOCAL_REFERENCE_DETAILS' ? merge({}, localReferenceDetailsConfig, props) : merge({}, currentAddressDetailsConfig, props), [props]),
    variant = _useMemo.variant,
    testID = _useMemo.testID,
    _state = _useMemo.state,
    successStateHeading = _useMemo.successStateHeading,
    isExpanded = _useMemo.isExpanded,
    disableToggle = _useMemo.disableToggle,
    calloutProps = _useMemo.calloutProps,
    _onUserAction = _useMemo.onUserAction,
    id = _useMemo.id,
    onValidFormMetaData = _useMemo.onValidFormMetaData,
    onInValidFormMetaData = _useMemo.onInValidFormMetaData,
    autoFormSubmitDebounceDelay = _useMemo.autoFormSubmitDebounceDelay,
    isAutoFormSubmitDebounceAllowed = _useMemo.isAutoFormSubmitDebounceAllowed,
    onSubmitButtonPressMetaData = _useMemo.onSubmitButtonPressMetaData,
    onStepDetailsListItemPressMetaData = _useMemo.onStepDetailsListItemPressMetaData,
    onStepDetailsCtaPressMetaData = _useMemo.onStepDetailsCtaPressMetaData,
    inputsDataProps = _useMemo.inputsDataProps,
    stepDetailsProps = _useMemo.stepDetailsProps,
    submitButtonProps = _useMemo.submitButtonProps,
    _useMemo$removeHintTe = _useMemo.removeHintTextOnChange,
    removeHintTextOnChange = _useMemo$removeHintTe === void 0 ? true : _useMemo$removeHintTe,
    style = _useMemo.style;
  var mobileNumber = inputsDataProps.mobileNumber,
    name = inputsDataProps.name,
    houseNo = inputsDataProps.houseNo,
    street = inputsDataProps.street,
    area = inputsDataProps.area,
    landmark = inputsDataProps.landmark,
    pinCode = inputsDataProps.pinCode,
    city = inputsDataProps.city,
    district = inputsDataProps.district,
    state = inputsDataProps.state;
  var addressDetailsInitialState = useMemo(() => {
    var initialState = {
      nameInput: name,
      houseNoInput: houseNo,
      streetInput: street,
      areaInput: area,
      landmarkInput: landmark,
      pinCodeInput: pinCode,
      cityInput: city,
      districtInput: district,
      stateInput: state,
      mobileNumberInput: mobileNumber
    };
    if (variant === 'CURRENT_ADDRESS_DETAILS') {
      delete initialState.mobileNumberInput;
      delete initialState.nameInput;
    }
    return initialState;
  }, [mobileNumber, name, houseNo, street, area, landmark, pinCode, city, district, state]);
  var addressDetailsReducer = (state, action) => {
    var type = action.type,
      name = action.name,
      key = action.key,
      value = action.value,
      isValid = action.isValid;
    switch (type) {
      case AADHAAR_DETAILS_ACTIONS.UPDATE_ADDRESS_VALUE:
        {
          var prevState = state === null || state === void 0 ? void 0 : state[name];
          return Object.assign(Object.assign({}, state), {
            [name]: Object.assign(Object.assign({}, prevState), {
              [key]: value,
              isChanged: (prevState === null || prevState === void 0 ? void 0 : prevState.value) !== value,
              isValid: isValid !== null && isValid !== void 0 ? isValid : prevState === null || prevState === void 0 ? void 0 : prevState.isValid
            })
          });
        }
      case AADHAAR_DETAILS_ACTIONS.UPDATE_ADDRESS_ALL_DETAILS:
        {
          var _prevState = state === null || state === void 0 ? void 0 : state[name];
          return Object.assign(Object.assign({}, state), {
            [name]: Object.assign(Object.assign({}, _prevState), value)
          });
        }
      default:
        return state;
    }
  };
  var _useReducer = useReducer(addressDetailsReducer, addressDetailsInitialState),
    addressDetails = _useReducer[0],
    dispatchAddressDetails = _useReducer[1];
  var _useState = useState(isExpanded),
    isExpandedState = _useState[0],
    setIsExpandedState = _useState[1];
  var _useState2 = useState(_state),
    stateType = _useState2[0],
    setStateType = _useState2[1];
  var _useState3 = useState((_a = stepDetailsProps === null || stepDetailsProps === void 0 ? void 0 : stepDetailsProps.subHeading) !== null && _a !== void 0 ? _a : ''),
    subHeading = _useState3[0],
    setSubHeading = _useState3[1];
  var isDisabledState = stateType === 'disabled';
  var isSuccessState = stateType === 'success';
  var skycFormWidgetStyle = getSkycFormWidgetStyles(props);
  var addressWidgetStyle = getAddressStyles();
  var formInputValues = useMemo(() => getValuesFromAddressDetails(addressDetails), [addressDetails]);
  var isAddressDetailsValid = useMemo(() => {
    return Object.values(addressDetails).every(input => {
      if (!isValidObject(input)) return false;
      return (input === null || input === void 0 ? void 0 : input.isRequired) && (input === null || input === void 0 ? void 0 : input.isValid) || !(input === null || input === void 0 ? void 0 : input.isRequired);
    });
  }, [addressDetails]);
  var updateAllAddressDetails = (name, data) => {
    var value = data.value,
      restProps = __rest(data, ["value"]);
    dispatchAddressDetails({
      type: AADHAAR_DETAILS_ACTIONS.UPDATE_ADDRESS_ALL_DETAILS,
      name,
      value: restProps
    });
    handleInputValueChange(name, value);
  };
  useEffect(() => {
    setSubHeading(stepDetailsProps === null || stepDetailsProps === void 0 ? void 0 : stepDetailsProps.subHeading);
  }, [stepDetailsProps === null || stepDetailsProps === void 0 ? void 0 : stepDetailsProps.subHeading]);
  useEffect(() => {
    var isAddressDetailsChanged = Object.values(addressDetails).some(input => input === null || input === void 0 ? void 0 : input.isChanged);
    if (!isAddressDetailsChanged) return;
    setSubHeading(getSubHeading(addressDetails, stepDetailsProps === null || stepDetailsProps === void 0 ? void 0 : stepDetailsProps.subHeading));
    var metaData = isAddressDetailsValid ? onValidFormMetaData : onInValidFormMetaData;
    var userActionPayload = [Object.assign({
      id
    }, metaData), Object.assign({
      props
    }, formInputValues)];
    if (!isValidObject(metaData) || !_onUserAction) return;
    if (isAutoFormSubmitDebounceAllowed) {
      debouncedUserAction(...userActionPayload);
    } else {
      _onUserAction(...userActionPayload);
    }
  }, [isAddressDetailsValid, formInputValues]);
  useEffect(() => {
    setStateType(_state);
    setIsExpandedState(_state === 'none');
  }, [_state]);
  useEffect(() => {
    setIsExpandedState(isExpanded);
    if (!isExpanded) Keyboard.dismiss();
  }, [isExpanded]);
  useEffect(() => {
    if (variant !== 'CURRENT_ADDRESS_DETAILS') {
      dispatchAddressDetails({
        type: AADHAAR_DETAILS_ACTIONS.UPDATE_ADDRESS_ALL_DETAILS,
        name: 'mobileNumberInput',
        value: mobileNumber
      });
    }
  }, [JSON.stringify(mobileNumber)]);
  useEffect(() => {
    updateAllAddressDetails('pinCodeInput', pinCode);
  }, [JSON.stringify(pinCode)]);
  useEffect(() => {
    updateAllAddressDetails('stateInput', state);
  }, [JSON.stringify(state)]);
  useEffect(() => {
    updateAllAddressDetails('cityInput', city);
  }, [JSON.stringify(city)]);
  useEffect(() => {
    updateAllAddressDetails('districtInput', district);
  }, [JSON.stringify(district)]);
  var debouncedUserAction = useCallback(debounce(_onUserAction, autoFormSubmitDebounceDelay), []);
  var handleOnValidInputMetaData = (key, value) => {
    var inputProps = addressDetails === null || addressDetails === void 0 ? void 0 : addressDetails[key];
    if (inputProps === null || inputProps === void 0 ? void 0 : inputProps.onValidInputMetaData) {
      _onUserAction === null || _onUserAction === void 0 ? void 0 : _onUserAction(Object.assign({
        id
      }, inputProps.onValidInputMetaData), Object.assign(Object.assign({}, inputProps), {
        value
      }));
    }
  };
  var handleOnPressIn = name => {
    var onClickMetaData = addressDetails === null || addressDetails === void 0 ? void 0 : addressDetails[name].onClickMetaData;
    isValidObject(onClickMetaData) && (_onUserAction === null || _onUserAction === void 0 ? void 0 : _onUserAction(Object.assign({
      id
    }, onClickMetaData), Object.assign(Object.assign({}, addressDetails === null || addressDetails === void 0 ? void 0 : addressDetails[name]), {
      name
    })));
  };
  var handleInputValueChange = (name, value) => {
    if (value === undefined) return;
    dispatchAddressDetails({
      type: AADHAAR_DETAILS_ACTIONS.UPDATE_ADDRESS_VALUE,
      name,
      key: 'value',
      value,
      isValid: validateInput(addressDetails === null || addressDetails === void 0 ? void 0 : addressDetails[name], value)
    });
    var inputName = ['pinCodeInput', 'mobileNumberInput'];
    if (inputName.includes(name) && removeHintTextOnChange) {
      dispatchAddressDetails({
        type: AADHAAR_DETAILS_ACTIONS.UPDATE_ADDRESS_ALL_DETAILS,
        name,
        value: {
          state: 'none',
          hintText: ''
        }
      });
    }
    var onChangeMetaData = addressDetails === null || addressDetails === void 0 ? void 0 : addressDetails[name].onChangeMetaData;
    isValidObject(onChangeMetaData) && (_onUserAction === null || _onUserAction === void 0 ? void 0 : _onUserAction(Object.assign({
      id
    }, onChangeMetaData), Object.assign(Object.assign({}, addressDetails === null || addressDetails === void 0 ? void 0 : addressDetails[name]), {
      name,
      value
    })));
  };
  var getTitleComponent = () => _jsx(LinkListItemWidget, Object.assign({}, stepDetailsProps, isSuccessState && !isExpandedState && successStateHeading && {
    heading: successStateHeading
  }, getTestId(testID, variant + "_LinkListItemWidget"), {
    roundedCorners: true,
    theme: theme,
    noHoverEffect: true,
    noPressEffect: true,
    onPress: onStepDetailsListItemPressMetaData,
    onUserAction: _onUserAction,
    actionTypeProps: !isExpandedState && !isDisabledState && (stepDetailsProps === null || stepDetailsProps === void 0 ? void 0 : stepDetailsProps.actionTypeProps),
    actionTypeOnPressMetaData: onStepDetailsCtaPressMetaData
  }, isSuccessState && Object.assign({}, successAvatarConfig), isSuccessState && !isExpandedState && {
    subHeading
  }, {
    disabled: getIsDisabledState(isDisabledState, stepDetailsProps === null || stepDetailsProps === void 0 ? void 0 : stepDetailsProps.disabled),
    subHeadingNumberOfLines: (stepDetailsProps === null || stepDetailsProps === void 0 ? void 0 : stepDetailsProps.subHeadingNumberOfLines) || 1
  }));
  return isValidObject(stepDetailsProps) && _jsx(Collapsible, Object.assign({
    theme: theme,
    childPaddingToken: "SPACING_0",
    titlePaddingToken: "SPACING_0",
    withCard: true,
    showIcon: false,
    style: [skycFormWidgetStyle.heading, style],
    titleNoVerticalPadding: true,
    titleNoHorizontalPadding: true,
    isExpanded: isExpandedState,
    disabled: isDisabledState || disableToggle,
    titleComponent: getTitleComponent()
  }, getTestId(testID, variant + "_Collapsible"), {
    children: _jsxs(View, Object.assign({
      style: [addressWidgetStyle.content]
    }, {
      children: [isValidObject(calloutProps) && _jsx(Callout, Object.assign({}, calloutProps, {
        theme: theme,
        style: [skycFormWidgetStyle.calloutWidget, calloutProps.style]
      }, getTestId(testID, variant + "_Callout"))), Object.entries(addressDetails).map(inputItem => {
        var inputName = inputItem[0],
          inputProps = inputItem[1];
        if (isValidObject(inputProps)) {
          return _jsx(View, {
            children: _jsx(Input, Object.assign({}, inputProps, {
              theme: theme,
              containerStyle: [skycFormWidgetStyle.inputWidget, inputProps === null || inputProps === void 0 ? void 0 : inputProps.style],
              onChangeText: value => handleInputValueChange(inputName, value),
              onPressIn: () => handleOnPressIn(inputName),
              onValidInput: value => handleOnValidInputMetaData(inputName, value),
              isDisabled: isDisabledState || (inputProps === null || inputProps === void 0 ? void 0 : inputProps.isDisabled)
            }, getTestId(testID, variant + "_" + inputName + "_Input")))
          }, inputName);
        }
        return null;
      }), isValidObject(submitButtonProps) && _jsx(ButtonWidget, Object.assign({
        leftCta: Object.assign(Object.assign({}, submitButtonProps === null || submitButtonProps === void 0 ? void 0 : submitButtonProps.leftCta), {
          theme,
          disabled: isDisabledState || !isAddressDetailsValid
        }),
        onLeftCtaPress: onSubmitButtonPressMetaData,
        onUserAction: (actions, extraData) => {
          _onUserAction === null || _onUserAction === void 0 ? void 0 : _onUserAction(actions, Object.assign(Object.assign(Object.assign({}, extraData), submitButtonProps), formInputValues));
        }
      }, getTestId(testID, variant + "_ButtonWidget"), {
        style: addressWidgetStyle.button
      }))]
    }))
  }));
};